import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import type { Persister } from '@tanstack/react-query-persist-client';
import createClient, { type Middleware } from 'openapi-fetch';
import { type ReactNode, createContext } from 'react';
import { useSelector } from 'react-redux';
import type { paths } from '../../types/openapi';

import { BEEM_PERSISTED_STORE_BUSTER, cacheTime, reactQueryClient } from '../clients/api/client';
import { DEBUG_USER_ID } from '../clients/gccApiClient/gccApiClient';
import { getErrorMsg } from '../helper/errorMessages';

import { onRestoreCacheSuccess, setMutationsDefaults } from '../offline-settings/react-query-rehydrations';
import { PusherProvider } from '../pusher/pusher-context';
import { initPusher } from '../pusher/pusher-init';
import { getApiBaseUrl } from '../slices/theme/themeSelectors';

type ClientType = ReturnType<typeof createClient<paths>>;

export const ApiContext = createContext<ClientType | null>(null);

const createAuthMiddleware = (): Middleware => ({
  async onRequest(req) {
    const debugUserId = localStorage.getItem(DEBUG_USER_ID);
    if (debugUserId) {
      req.headers.set('X-TEST-ENV-ONLY-User-Id', debugUserId);
    }

    return req;
  },
});

const createErrorMiddlware = (): Middleware => ({
  async onResponse(res, _options, req) {
    if (res.status >= 400) {
      // Skip error handling for GET methods to handle them in the queryFn
      // TODO: unify error handling in the future so this ugly hack is not needed
      if (req.method === 'GET' && !(res.url.endsWith('current-user') && res.status === 401)) {
        return undefined;
      }
      const error = await res.clone().json();
      const { error_code: errorCode, description } = error;
      const errorMessage = (getErrorMsg(errorCode) ||
        description ||
        getErrorMsg('chat.default') ||
        'Unbekannter Fehler') as string;
      throw new Error(errorMessage);
    }
    return undefined;
  },
});

interface PersistedQueryProviderProps {
  children: ReactNode;
  persister: Persister;
}

const PersistedQueryProvider = (props: PersistedQueryProviderProps) => {
  const { children, persister } = props;

  setMutationsDefaults();

  return (
    <PersistQueryClientProvider
      client={reactQueryClient}
      persistOptions={{ persister, maxAge: cacheTime, buster: BEEM_PERSISTED_STORE_BUSTER }}
      onSuccess={onRestoreCacheSuccess}
    >
      {children}
      {import.meta.env.VITE_SHOW_REACT_QUERY_DEVTOOLS === 'true' && <ReactQueryDevtools position="left" />}
    </PersistQueryClientProvider>
  );
};

interface ApiProviderProps {
  children: ReactNode;
  persister: Persister;
}

const ApiProvider = (props: ApiProviderProps) => {
  const { children, persister } = props;

  const baseUrl = useSelector(getApiBaseUrl);
  /**
   * There are two clients here:
   * - reactQueryClient - the React query client
   * - openApiFetchClient - typed fetch client (openapi-fetch) using the OpenAPi spec from our API
   * ReactQueryClient is using the OpenApiFetchClient in the queryFn to make HTTP requests.
   */
  const appVersionNumber = import.meta.env.VITE_BEEM_VERSION_NUMBER;
  const openApiFetchClient = createClient<paths>({
    baseUrl,
    credentials: 'include',
    headers: {
      'X-BEEM-MOBILE-VERSION': appVersionNumber,
    },
  });

  const pusher = initPusher({
    pusherKey: import.meta.env.VITE_PUSHER_KEY,
    isDev: import.meta.env.DEV,
    baseUrl,
  });

  openApiFetchClient.use(createAuthMiddleware());
  openApiFetchClient.use(createErrorMiddlware());

  return (
    <ApiContext.Provider value={openApiFetchClient}>
      <PersistedQueryProvider persister={persister}>
        <PusherProvider pusher={pusher}>{children}</PusherProvider>
      </PersistedQueryProvider>
    </ApiContext.Provider>
  );
};

export default ApiProvider;
